import * as React from 'react';
import { Card, Icon, Button, Message } from 'semantic-ui-react';
import './ListInput.scss';

const Sortable = require('react-anything-sortable');

export interface ComponentProps {
    value: any;
    initialValues?: any[];
    onChange: Function;
}

export interface StagingComponentProps extends ComponentProps {
    onAdd?: (event: any) => any;
}

export interface ItemComponentProps extends ComponentProps {
    onRemove?: (event: any) => any;
}

export interface ItemInput extends ItemComponentProps {
    removable: boolean;
    decorateHandle: (input: JSX.Element) => any;
}

export interface StagingInput extends StagingComponentProps {
    add: any;
}

export interface ListInputProps {
    name: any;
    defaultValue: any;
    initialValues: any[];
    validate: (item: any) => any;
    Item: (params: ItemComponentProps) => JSX.Element;
    StagingItem: (params: StagingComponentProps) => JSX.Element;
    maxItems?: number;
    minItems?: number;
    onValuesChange?: (values: any[]) => void;
    sortValue?: (values: any[]) => void;
    handleAdd?: (e: any, value: any) => void;
    handleDelete?: (e: any, value: any) => void;
    addIcon?: string;
    addButtonText?: string;
    className?: string;
    customEditEvent?: boolean;
}

export interface ListInputState {
    value: Array<any>;
}

export type BasicCompoClassProps = StagingComponentProps & ItemComponentProps & { [key: string]: any};
export abstract class BaseComponentClass extends React.Component<BasicCompoClassProps, any> {
    changeHandler = this.onInputChange.bind(this);
    constructor(props: any) {
        super(props);
        this.state = this.props.value || {};
    }
    onInputChange(e: any, {name, value}: {name: string, value: any} ) {
        this.setState(
            {
                ...this.state,
                [name] : value
            },
            this.notifyChange.bind(this)
        );
    }

    handleChecked (e: any, {checked, name}: any) {
        this.setState(
            {
                ...this.state,
                [name] : checked
            },
            this.notifyChange.bind(this)
        );
    }

    notifyChange() {
        this.props.onChange(this.state);
    }
}

export class ListInput extends React.Component<ListInputProps, any> {
    constructor (props: ListInputProps) {
        super(props);
        
        this.state = {
            values: props.initialValues || [],
            stagingValue: typeof this.props.defaultValue === 'function' ?
                this.props.defaultValue() 
                : this.props.defaultValue,
            addValidation: true,
            recentlyAdded: false
        };
    }

    handleAddElement() {
        const { values, stagingValue } = this.state;
        
        const validation = this.props.validate(stagingValue);
        
        if ( validation === true) {
            
            let endValues = [... values, stagingValue];
            this.setState({
                values: endValues,
                stagingValue: typeof this.props.defaultValue === 'function' ?
                    this.props.defaultValue() 
                    : this.props.defaultValue,
                addValidation: true,
                recentlyAdded: true
            });
            if (this.props.handleAdd ) {
                this.props.handleAdd({}, stagingValue);
            }
            // this is to remount the inputs so they reload default value
            setTimeout((() => this.setState({recentlyAdded: false})).bind(this), 500);

            this.props.onValuesChange(endValues);
        } else {
            
            this.setState({
                addValidation: validation
            });
        }
    }

    handleRemoveElement(index: number) {
        const { values } = this.state;
        if (index >= 0 && index < values.length) {
            const endValues = [...values.slice(0, index), ...values.slice(index + 1, values.length)];
            if (this.props.handleDelete) {
                this.props.handleDelete(null, values[index]);
            }

            this.setState({
                values: endValues
            });
            
            this.props.onValuesChange(endValues);
        }
    }

    handleStagingUpdate(stagingValue: any) {
        if (this.state.addValidation === true) {
            this.setState({ stagingValue });
        } else {
            this.setState({
                stagingValue,
                addValidation: this.props.validate(stagingValue)
            });
        }
        
    }

    handleValueUpdate(index: number, value: any) {
        const validation = this.props.validate(value);
        const { values } = this.state;

        if (validation === true) {

            let endValues = [...values.slice(0, index), value, ...values.slice(index + 1, values.length)];
            this.setState({
                values: endValues
            });

            this.props.onValuesChange(endValues);
            
            return true;
        }
        return validation;
    }

    Item (value: any, index: number) {
        const onChange = this.handleValueUpdate.bind(this, index);
        const onRemove = this.handleRemoveElement.bind(this, index);
        const initialValues = this.props.initialValues;
        return (
            <Sortable.SortableContainer
                key={`${index}-${value.typedId}`}
                sortData={value}
                className="sortable-list-input-container"
            >
                <Card className={this.props.name}>
                    <Card.Content className="handle sortable-card-header-content">
                        <Card.Header className="sortable-card-header">
                            <div className="drag-symbol">
                                <Icon name="move" /> Drag to move
                            </div>
                            <Button 
                                icon="trash" 
                                content="delete" 
                                color="red" 
                                floated="right" 
                                type="button"
                                onClick={onRemove}
                                size="small"
                                className="absolute right delete-button"
                            />
                        
                        </Card.Header>
                    </Card.Content>
                       
                    <Card.Content>
                       {this.props.Item({ onChange, value, initialValues })}
                    </Card.Content>
                </Card>
            </Sortable.SortableContainer>
        );
    }

    StagingItem () {
        const onAdd = this.handleAddElement.bind(this);
        const onChange = this.handleStagingUpdate.bind(this);
        const ValidationMessage = this.state.addValidation !== true ? 
            (typeof this.state.addValidation === 'string' ?
                <Message negative> {this.state.addValidation}</Message>
                : <Message {...this.state.addValidation} negative />
            )
            
            : null;
        if (this.state.recentlyAdded === true) {
            return null;
        } else {
            return (
                <Card key="add-new">
                    <Card.Content>
                        <Card.Header>
                            <Icon name="add circle" /> Add New
                        </Card.Header>
                    </Card.Content>
                    <Card.Content>
                        {ValidationMessage}
                        {this.props.StagingItem({ onChange, onAdd, value: this.state.stagingValue})}
                    </Card.Content>
                    <Card.Content>
                        <Button 
                            type="button"
                            primary
                            onClick={onAdd}
                            disabled={this.state.addValidation !== true}
                            icon={this.props.addIcon || 'add'}
                            content={this.props.addButtonText || 'Add New'}
                            className="absolute right top-margin add"
                        />
                    </Card.Content>
                </Card>
            );  
        }
    }

    handleSort(_values: any) {
        const values = this.props.sortValue ? this.props.sortValue(_values) : _values;
        this.setState({values});
        this.props.onValuesChange(values);
    }

    render() {
        if (this.props.maxItems && this.props.maxItems <= 1) {
            return this.renderOne();
        } else {
            return this.renderMany();
        }
    }
    renderOne() {
        const value = this.state.values[0];
        const onChange = this.handleValueUpdate.bind(this, 0);
        return this.props.Item({ onChange, value });
    }

    renderMany () {

        return (
            <div className={`list-input-container ${this.props.className}`}>
                <Card.Group>
                    <Sortable 
                        onSort={this.handleSort.bind(this)} 
                        sortHandle="handle"
                        containment
                        dynamic
                    >
                        {this.state.values.map(this.Item.bind(this), this)}
                    </Sortable>
                    {(
                            typeof this.props.maxItems === 'undefined' 
                            || this.state.values <= this.props.maxItems 
                        ) ? this.StagingItem() : null}
                    
                </Card.Group>
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/generic/ListInput.tsx